/** Normalize combo line items from API or catalog into a consistent shape. */
export function normalizeComboItems(combo) {
  if (!combo?.items?.length) return [];

  return combo.items
    .map((item) => {
      const productId = Number(item.product ?? item.product_id);
      if (!productId || Number.isNaN(productId)) return null;

      return {
        productId,
        quantity: Number(item.quantity) || 1,
        name: item.product_name || item.name || 'Product',
        slug: item.product_slug || item.slug || '',
        price: String(item.product_price ?? item.price ?? '0'),
        image_url: item.image_url || '',
      };
    })
    .filter(Boolean);
}

/** Add or increment a single product in the cart (pure function). */
export function mergeProductIntoCart(prevItems, product, quantity = 1, customName = '') {
  const productId = Number(product.id);
  const existingItem = prevItems.find((item) =>
    Number(item.product.id) === productId &&
    (item.customName || '') === (customName || '')
  );

  if (existingItem) {
    return prevItems.map((item) =>
      Number(item.product.id) === productId &&
      (item.customName || '') === (customName || '')
        ? { ...item, quantity: item.quantity + quantity }
        : item
    );
  }

  const cartItemId = `${productId}_${customName || ''}_${Date.now()}`;
  return [
    ...prevItems,
    {
      product: { ...product, id: productId },
      quantity,
      customName: customName || '',
      id: cartItemId,
    },
  ];
}

/** Merge bundle items into an existing cart array (pure function). */
export function mergeComboIntoCart(prevItems, combo) {
  const comboItems = normalizeComboItems(combo);
  if (!comboItems.length) {
    return { items: prevItems, addedCount: 0 };
  }

  const cartQtyByProduct = prevItems.reduce((acc, item) => {
    const pid = Number(item.product?.id);
    if (!pid || Number.isNaN(pid)) return acc;
    acc[pid] = (acc[pid] || 0) + item.quantity;
    return acc;
  }, {});

  let newItems = [...prevItems];
  let addedCount = 0;

  for (const item of comboItems) {
    const inCart = cartQtyByProduct[item.productId] || 0;
    const needed = item.quantity - inCart;
    if (needed <= 0) continue;

    addedCount += needed;
    cartQtyByProduct[item.productId] = inCart + needed;

    const existingItem = newItems.find(
      (cartItem) =>
        Number(cartItem.product?.id) === item.productId && !(cartItem.customName || '')
    );

    if (existingItem) {
      newItems = newItems.map((cartItem) =>
        Number(cartItem.product?.id) === item.productId && !(cartItem.customName || '')
          ? { ...cartItem, quantity: cartItem.quantity + needed }
          : cartItem
      );
    } else {
      newItems.push({
        product: {
          id: item.productId,
          name: item.name,
          slug: item.slug,
          price: item.price,
          image_url: item.image_url,
          is_available: true,
        },
        quantity: needed,
        customName: '',
        id: `${item.productId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      });
    }
  }

  return { items: newItems, addedCount };
}

export function loadCartFromStorage() {
  const savedCart = localStorage.getItem('cart');
  if (!savedCart) return [];

  try {
    const parsed = JSON.parse(savedCart);
    if (Array.isArray(parsed) && parsed.every((item) => item.product && item.quantity)) {
      return parsed;
    }
    localStorage.removeItem('cart');
    return [];
  } catch (error) {
    console.error('Error parsing cart from localStorage:', error);
    localStorage.removeItem('cart');
    return [];
  }
}

export function saveCartToStorage(items) {
  if (items.length > 0) {
    localStorage.setItem('cart', JSON.stringify(items));
  } else {
    localStorage.removeItem('cart');
  }
}

const COMBO_VARIANTS_KEY = 'comboBoxVariants';

export function loadComboBoxVariants() {
  try {
    const raw = localStorage.getItem(COMBO_VARIANTS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function saveComboBoxVariants(variants) {
  if (variants && Object.keys(variants).length > 0) {
    localStorage.setItem(COMBO_VARIANTS_KEY, JSON.stringify(variants));
  } else {
    localStorage.removeItem(COMBO_VARIANTS_KEY);
  }
}

export function setComboBoxVariant(slug, boxVariant) {
  const next = { ...loadComboBoxVariants(), [slug]: boxVariant };
  saveComboBoxVariants(next);
  return next;
}
